/**
 * misinfoBank.js — pre-curated Clickbait Slayer claims, free forever.
 *
 * Replaces the paid LLM-generated claims. Each claim has: difficulty,
 * category, claim text, is_real flag, and a kid-friendly explanation.
 * Picked at random within the requested difficulty; tracks recently-shown
 * IDs in memory so we don't repeat within a short streak.
 */

export const MISINFO_BANK = [
  // ============= EASY (Apprentice) =============
  { id: "e1", difficulty: "easy", category: "the human brain",
    claim: "We only use 10% of our brain.",
    is_real: false,
    explanation: "FAKE! 🚨 Brain scans prove we use almost ALL of our brain, just not all at the same time. Different tasks light up different regions, but no part is 'unused.'",
  },
  { id: "e2", difficulty: "easy", category: "outer space",
    claim: "A day on Venus is longer than a year on Venus.",
    is_real: true,
    explanation: "TRUE! 🪐 Venus spins so slowly that one full rotation takes 243 Earth days, while it orbits the Sun in only 225 Earth days. So a day really is longer than a year there!",
  },
  { id: "e3", difficulty: "easy", category: "the human heart",
    claim: "The human heart can keep beating for a short time even outside the body.",
    is_real: true,
    explanation: "TRUE! 🫀 The heart has its own electrical system. Removed from the body, with the right oxygen and nutrients, it can keep beating on its own for several minutes — this is exactly why heart transplants are possible.",
  },
  { id: "e4", difficulty: "easy", category: "animals",
    claim: "Bats are blind.",
    is_real: false,
    explanation: "FAKE! 🦇 Bats can see just fine — many species see better than humans in low light. They use echolocation in addition to sight, not instead of it.",
  },
  { id: "e5", difficulty: "easy", category: "weather",
    claim: "Lightning never strikes the same place twice.",
    is_real: false,
    explanation: "FAKE! ⚡ Lightning hits the same spot ALL THE TIME. The Empire State Building in New York gets struck about 25 times every single year.",
  },
  { id: "e6", difficulty: "easy", category: "dinosaurs",
    claim: "T-Rex had feathers.",
    is_real: true,
    explanation: "TRUE! 🦖 Fossil evidence shows many T-Rex relatives had feathers, and adult T-Rex likely had at least some feathers, especially as babies. They weren't fluffy chickens but they weren't lizard-smooth either.",
  },
  { id: "e7", difficulty: "easy", category: "ocean life",
    claim: "The Great Wall of China is visible from space with the naked eye.",
    is_real: false,
    explanation: "FAKE! 🌍 Even from low Earth orbit, the Great Wall is too narrow to see without zoom. Astronauts have confirmed this. From the Moon? Absolutely not.",
  },
  { id: "e8", difficulty: "easy", category: "the human body",
    claim: "Your stomach gets a new lining every few days.",
    is_real: true,
    explanation: "TRUE! 🫁 Your stomach makes acid strong enough to dissolve metal. To protect itself, it replaces its lining every 3-5 days. The cells are constantly regenerating.",
  },
  { id: "e9", difficulty: "easy", category: "plants and trees",
    claim: "Goldfish only have a 3-second memory.",
    is_real: false,
    explanation: "FAKE! 🐠 Studies show goldfish remember things for MONTHS, not seconds. They can be trained, recognize their owners, and even tell time around feeding schedules.",
  },
  { id: "e10", difficulty: "easy", category: "outer space",
    claim: "Mars looks red because it's covered in rust.",
    is_real: true,
    explanation: "TRUE! 🟠 The Martian surface is full of iron oxide — chemically the same as rust on a bike. Billions of years of iron reacting with tiny amounts of oxygen gave Mars its color.",
  },

  // ============= HARD (Sciencer) =============
  { id: "h1", difficulty: "hard", category: "the human brain",
    claim: "The brain doesn't feel pain.",
    is_real: true,
    explanation: "TRUE! 🧠 The brain itself has no pain receptors. Surgeons can perform brain surgery while the patient is awake — they only need to numb the scalp and skull. The brain monitors pain everywhere else but can't feel it on itself.",
  },
  { id: "h2", difficulty: "hard", category: "physics",
    claim: "Sound travels faster in water than in air.",
    is_real: true,
    explanation: "TRUE! 🌊 Sound moves at about 343 m/s in air but ~1,480 m/s in water — over 4 times faster. Water molecules are packed tighter, so vibrations pass between them more efficiently.",
  },
  { id: "h3", difficulty: "hard", category: "animals",
    claim: "Honey never spoils — archaeologists found edible 3,000-year-old honey in Egyptian tombs.",
    is_real: true,
    explanation: "TRUE! 🍯 Honey has very low water content and is naturally acidic, which prevents bacteria from growing. Sealed honey can stay edible for thousands of years.",
  },
  { id: "h4", difficulty: "hard", category: "the human heart",
    claim: "Your heart pumps about 2,000 gallons of blood every day.",
    is_real: true,
    explanation: "TRUE! 🫀 Roughly 7,500 liters daily, beating about 100,000 times — nonstop, for your entire life. Over 80 years, that's enough blood to fill an Olympic swimming pool... about 10 times.",
  },
  { id: "h5", difficulty: "hard", category: "outer space",
    claim: "There are more trees on Earth than stars in the Milky Way.",
    is_real: true,
    explanation: "TRUE! 🌳 Earth has roughly 3 trillion trees. The Milky Way has 100-400 billion stars. So we're outnumbered by trees by about 10 to 1!",
  },
  { id: "h6", difficulty: "hard", category: "chemistry",
    claim: "Diamonds are forever — they last for billions of years without breaking down.",
    is_real: false,
    explanation: "FAKE! 💎 Diamonds slowly convert to graphite at room temperature. It takes BILLIONS of years, but they're not eternal. Plus, you can shatter one with a hammer — diamond is hard, not tough.",
  },
  { id: "h7", difficulty: "hard", category: "animals",
    claim: "Sharks are immune to cancer.",
    is_real: false,
    explanation: "FAKE! 🦈 This myth was used to sell shark cartilage as 'medicine.' Sharks absolutely DO get cancer — there are documented tumors in over 40 shark species. The myth led to overfishing for fake cures.",
  },
  { id: "h8", difficulty: "hard", category: "weather",
    claim: "It can be too cold to snow.",
    is_real: false,
    explanation: "FAKE! ❄️ It can snow at any temperature below 0°C. Very cold air holds less moisture so snow becomes less likely — but it never becomes IMPOSSIBLE. Antarctica gets snow at -50°C.",
  },
  { id: "h9", difficulty: "hard", category: "ocean life",
    claim: "Octopuses have three hearts and blue blood.",
    is_real: true,
    explanation: "TRUE! 🐙 Two hearts pump blood to the gills, the third pumps blood through the body. Their blood is blue because it uses copper-based hemocyanin to carry oxygen, instead of iron-based hemoglobin like us.",
  },
  { id: "h10", difficulty: "hard", category: "physics",
    claim: "Hot water freezes faster than cold water under certain conditions.",
    is_real: true,
    explanation: "TRUE! 🧊 This is the Mpemba effect, named after a Tanzanian student. Scientists still debate WHY — evaporation, dissolved gases, and convection currents all play a role. It's been replicated in labs.",
  },

  // ============= GENIUS (Neuro-Cardio Genius) =============
  { id: "g1", difficulty: "genius", category: "neuroscience",
    claim: "The myelin sheath around neurons makes signals travel up to 200 times faster than unmyelinated nerves.",
    is_real: true,
    explanation: "TRUE! 🧠 Myelinated axons conduct at ~120 m/s vs. ~0.5-2 m/s for unmyelinated. This 'saltatory conduction' jumps between gaps called nodes of Ranvier. Multiple sclerosis is what happens when myelin breaks down.",
  },
  { id: "g2", difficulty: "genius", category: "biology",
    claim: "Mitochondria have their own DNA, separate from the cell nucleus, because they descended from ancient bacteria that were engulfed about 2 billion years ago.",
    is_real: true,
    explanation: "TRUE! 🦠 This is the endosymbiotic theory, confirmed by genetic evidence. Mitochondrial DNA is circular like bacterial DNA, has its own ribosomes, and you inherit it ONLY from your mother — your dad's mitochondrial DNA isn't passed on.",
  },
  { id: "g3", difficulty: "genius", category: "cardiology",
    claim: "The vagus nerve carries 80% of its signals FROM the heart and gut TO the brain, not the other way around.",
    is_real: true,
    explanation: "TRUE! 🫀 The vagus nerve has 100,000+ fibers, and 80% are afferent — meaning they send signals UP to the brain. Sean calls this the Neurocardio Loop: the heart and gut are constantly informing the brain, not just receiving orders from it.",
  },
  { id: "g4", difficulty: "genius", category: "physics",
    claim: "The Higgs boson was confirmed by CERN in 2008.",
    is_real: false,
    explanation: "FAKE — wrong year! ⚛️ The Large Hadron Collider only started operations in 2008. The Higgs boson discovery wasn't announced until July 4th, 2012. Peter Higgs won the Nobel Prize for it in 2013.",
  },
  { id: "g5", difficulty: "genius", category: "neuroscience",
    claim: "Glial cells in your brain outnumber neurons by a factor of 10 to 1.",
    is_real: false,
    explanation: "FAKE! 🧠 This was textbook 'fact' for decades, but a 2009 study by Suzana Herculano-Houzel using a new counting method showed the ratio is actually closer to 1:1. Glia are still essential, just not 10x more numerous.",
  },
  { id: "g6", difficulty: "genius", category: "biology",
    claim: "CRISPR-Cas9 was first identified as a bacterial immune system before being adapted for gene editing.",
    is_real: true,
    explanation: "TRUE! 🧬 CRISPR (Clustered Regularly Interspaced Short Palindromic Repeats) was first observed in bacteria in 1987, but its function as a bacterial defense against viruses wasn't proven until 2007. Jennifer Doudna and Emmanuelle Charpentier won the 2020 Nobel Prize for repurposing it.",
  },
  { id: "g7", difficulty: "genius", category: "astrophysics",
    claim: "Neutron stars are so dense that a teaspoon of their material would weigh about 6 billion tons on Earth.",
    is_real: true,
    explanation: "TRUE! ⭐ Neutron stars cram more mass than the Sun into a sphere only ~20 km across. Their density is ~10¹⁴ g/cm³ — about a billion tons per cubic centimeter. They're essentially giant atomic nuclei.",
  },
  { id: "g8", difficulty: "genius", category: "cardiology",
    claim: "The Frank-Starling law states that the heart pumps weaker when filled with more blood.",
    is_real: false,
    explanation: "FAKE — backwards! 🫀 The Frank-Starling law says the heart pumps HARDER when its ventricles are stretched MORE by incoming blood. It's why your heart can scale output during exercise. Pre-load up, contraction up.",
  },
  { id: "g9", difficulty: "genius", category: "chemistry",
    claim: "Water expands when it freezes because ice is less dense than liquid water — one of the few substances that does this.",
    is_real: true,
    explanation: "TRUE! 💧 Hydrogen bonds in ice form an open hexagonal lattice that takes more space than liquid water's looser arrangement. This is WHY ice floats — and why life in lakes can survive frozen winters under an insulating ice layer.",
  },
  { id: "g10", difficulty: "genius", category: "neuroscience",
    claim: "Mirror neurons fire both when you perform an action AND when you watch someone else perform that same action.",
    is_real: true,
    explanation: "TRUE! 🪞 Discovered in 1992 by Giacomo Rizzolatti's team studying macaque monkeys. Mirror neurons are believed to underlie empathy, language learning, and imitation. Watching someone yawn really does activate the 'yawn' circuitry in your brain.",
  },
];

// Track recently-served claim IDs to avoid back-to-back repeats
let recentIds = [];
const RECENT_CAP = 5;

export function pickClaim(difficulty = "easy") {
  const pool = MISINFO_BANK.filter((c) => c.difficulty === difficulty);
  // Filter out recent ones if we have enough remaining
  const fresh = pool.filter((c) => !recentIds.includes(c.id));
  const candidates = fresh.length >= 2 ? fresh : pool;
  const chosen = candidates[Math.floor(Math.random() * candidates.length)];
  recentIds = [chosen.id, ...recentIds].slice(0, RECENT_CAP);
  return chosen;
}

export function checkAnswer(claim, userAnswer) {
  // userAnswer is a boolean: true = "REAL", false = "FAKE"
  const correct = claim.is_real === userAnswer;
  return {
    correct,
    verdict: correct ? "✅ Correct!" : "❌ Not quite!",
    is_real: claim.is_real,
    explanation: claim.explanation,
  };
}
